<template>
    <v-menu
        v-model="instanceMenu"
        offset-x
        v-if="showInstanceMenu"
        :disabled="isMenuDisabled"
        :close-on-content-click="false"
        min-width="300"
        nudge-top="8"
        :class="{ 'text--disabled': isSpaceRested || isSpaceArchived }">
        <template v-slot:activator="{ on: menuOn }">
            <v-tooltip right :disabled="!collapsed">
                <template v-slot:activator="{ on: tooltipOn }">
                    <v-list-item
                        :disabled="fetchingInstances || isMenuDisabled"
                        v-on="{ ...menuOn, ...tooltipOn }"
                        @mouseenter="isMenuActivatorHovered = true"
                        @mouseleave="isMenuActivatorHovered = false"
                        :input-value="Boolean(activeInstanceIid)"
                        active-class="primary--text">
                        <v-list-item-icon>
                            <v-icon>{{ isMenuActivatorHovered ? 'mdi-chevron-right' : currentInstanceTypeIcon }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content v-if="fetchingInstances">
                            <v-progress-linear indeterminate rounded></v-progress-linear>
                        </v-list-item-content>
                        <v-list-item-content v-else>
                            <v-list-item-title :class="{ 'text--disabled': isSpaceRested || isSpaceArchived }">
                                <b>{{ instanceName }}</b>
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="showQuickActions && !collapsed" class="my-0">
                            <v-menu v-model="instanceActionsMenu" offset-x right nudge-top="10">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn text small class="px-2" min-width="0" v-bind="attrs" v-on="on" @click.stop.prevent>
                                        <v-icon small>more_horiz</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense nav>
                                    <v-list-item @click="goToInstanceSettings()">
                                        <v-list-item-icon>
                                            <v-icon>mdi-cog-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Settings</v-list-item-title>
                                    </v-list-item>
                                    <v-divider class="my-2"></v-divider>
                                    <instance-delete-dialog
                                        v-if="canDeleteActiveInstance"
                                        :instance-id="parseInt(activeInstanceData.iid, 10)"
                                        :instance-name="activeInstanceData.long_id"
                                        :master-instance-id="masterInstance.iid.toString()"
                                        :master-development-snapshot-id="masterInstance.dev_snid.toString()">
                                        <template v-slot:activator="{ attrs, disabled, open }">
                                            <v-list-item v-bind="attrs" :disabled="disabled" @click.stop="open">
                                                <v-list-item-icon>
                                                    <v-icon :color="disabled ? null : 'error'" :class="disabled ? 'text--disabled' : ''">
                                                        mdi-trash-can-outline
                                                    </v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title :class="[disabled ? 'text--disabled' : 'error--text', 'mr-4']">
                                                    Delete instance
                                                </v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </instance-delete-dialog>
                                    <v-list-item v-else disabled>
                                        <v-list-item-icon>
                                            <v-icon color="grey">mdi-trash-can-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Delete instance</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </v-list-item-action>
                    </v-list-item>
                </template>
                <span>{{ instanceName }}</span>
            </v-tooltip>
        </template>

        <v-list nav dense>
            <v-list-item
                active-class="primary--text"
                v-if="masterInstance"
                :input-value="isInstanceSelected(masterInstance.iid)"
                @click="openInstance(masterInstance.long_id, masterInstance.iid, masterInstance.role)">
                <v-list-item-icon>
                    <v-icon>{{ getInstanceTypeIcon(masterInstance.long_id) }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title :class="{ 'text--disabled': isSpaceRested || isSpaceArchived }">
                        <b>{{ masterInstance.long_id }}</b>
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>

            <v-list-item
                active-class="primary--text"
                v-if="currentSpaceType === spaceTypes.EDUCATION_SPACE && distributedInstance"
                :input-value="isInstanceSelected(distributedInstance.iid)"
                @click="openInstance(distributedInstance.long_id, distributedInstance.iid, distributedInstance.role)">
                <v-list-item-icon>
                    <v-icon>{{ getInstanceTypeIcon(distributedInstance.long_id) }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title :class="{ 'text--disabled': isSpaceRested || isSpaceArchived }">
                        {{ distributedInstance.long_id }}
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>

            <v-divider class="my-2"></v-divider>

            <v-list-item class="px-0">
                <v-list-item-title>
                    <v-text-field
                        v-model="instanceSearch"
                        placeholder="Filter instances..."
                        background-color="grey lighten-4"
                        prepend-inner-icon="mdi-magnify "
                        autofocus
                        hide-details
                        solo
                        flat
                        dense></v-text-field>
                </v-list-item-title>
            </v-list-item>

            <v-list-item>
                <v-list-item-title>INSTANCES</v-list-item-title>
            </v-list-item>

            <template v-if="sortedInstances.length">
                <v-list-item
                    v-for="item in sortedInstances"
                    active-class="primary--text"
                    :key="item.iid"
                    :input-value="isInstanceSelected(item.iid)"
                    @click="openInstance(item.long_id, item.iid, item.role)"
                    :disabled="disabledInstance(item.data)">
                    <v-list-item-content>
                        <v-list-item-title :class="{ 'text--disabled': isSpaceRested || isSpaceArchived }">{{ item.long_id }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon small color="grey" class="ml-3" v-bind="attrs" v-on="on">{{ instanceRole(item.role).icon }}</v-icon>
                            </template>
                            <span>{{ instanceRole(item.role).label }}</span>
                        </v-tooltip>
                    </v-list-item-action>
                </v-list-item>
            </template>

            <v-list-item v-else-if="sortedInstances.length === 0 && !instanceSearch">
                <v-list-item-title>
                    <v-card flat>
                        <v-card-text class="text-center">
                            <p class="text--disabled">Looks like you have no instances yet.</p>
                        </v-card-text>
                    </v-card>
                </v-list-item-title>
            </v-list-item>

            <v-list-item v-else-if="instanceSearch && sortedInstances.length === 0">
                <v-list-item-title>
                    <dashboard-empty icon="mdi-information-outline">
                        No matching instances found.
                        <br />
                        Try a different filter.
                    </dashboard-empty>
                </v-list-item-title>
            </v-list-item>

            <v-divider class="my-2"></v-divider>
            <v-list-item class="px-0">
                <v-list-item-title>
                    <v-btn block color="grey lighten-4" @click="gotoInstances()">View all instances</v-btn>
                </v-list-item-title>
            </v-list-item>
        </v-list>
    </v-menu>
</template>

<script>
import { sortArray, isEditorRole, isViewerRole, isDistributedInstance, isMasterOrDistributedInstance, getInstanceTypeIcon, instanceRole } from '@/utils'
import { mapGetters, mapState } from 'vuex'
import { orderBy } from 'lodash'
import { DateTime } from 'luxon'
import { enumsData } from '@/mixins/enums'

const DashboardEmpty = () => import('@/components/DashboardEmpty.vue')
const InstanceDeleteDialog = () => import('@/modules/instance/components/TheInstanceDeleteDialog.vue')
const unixEpoch = DateTime.fromMillis(0).toISO()

export default {
    name: 'InstanceMenu',
    components: {
        DashboardEmpty,
        InstanceDeleteDialog
    },
    mixins: [enumsData],
    props: {
        collapsed: {
            type: Boolean,
            default: false
        },
        showQuickActions: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            instanceMenu: false,
            instanceSearch: '',
            instanceActionsMenu: false,
            isMenuActivatorHovered: false,
            loadedConfigInstancesSid: null
        }
    },
    computed: {
        ...mapState('orgStore', ['fetchingSpaces']),
        ...mapState('spaceStore', ['spaceInstances', 'fetchingInstances', 'masterInstance', 'distributedInstance', 'otherInstances']),
        ...mapState('instanceStore', ['instanceSnapshots']),
        ...mapGetters('spaceStore', ['currentSpaceType', 'isSpaceArchived', 'isSpaceRested', 'isSpaceAdmin']),
        selectedInstance() {
            if (this.$route.params.iid === undefined) {
                return null
            }
            return this.spaceInstances.find(instance => instance.iid.toString() === this.$route.params.iid.toString()) || null
        },
        currentInstanceTypeIcon() {
            return getInstanceTypeIcon(this.selectedInstance?.long_id)
        },
        instanceName() {
            if (this.$route.params.iid !== undefined && this.spaceInstances && this.spaceInstances.length) {
                return this.$store.getters['instanceStore/instanceLongNameById'](this.$route.params.iid)
            }
            return 'Select instance'
        },
        sortedInstances() {
            const instances = orderBy(this.otherInstances, instance => instance.creation_timestamp || unixEpoch, 'desc')
            if (this.instanceSearch) {
                return instances.filter(instance => instance.long_id.toLowerCase().indexOf(this.instanceSearch.toLowerCase()) !== -1)
            }
            return instances.slice(0, 5)
        },
        isConfigRoute() {
            return (this.$route.name || '').startsWith('config')
        },
        isMenuDisabled() {
            if (!this.isConfigRoute) {
                return false
            }
            return this.disabled || this.$route.params.sid === undefined || this.fetchingSpaces
        },
        showInstanceMenu() {
            if (this.isConfigRoute) {
                return true
            }
            if (!this.fetchingInstances && !this.fetchingSpaces && this.$route.params.snid !== undefined) {
                return true
            }
            return false
        },
        activeOrgOid() {
            return this.$route.params.oid
        },
        activeSpaceSid() {
            return this.$route.params.sid
        },
        activeInstanceIid() {
            return this.$route.params.iid || this.selectedInstance?.iid
        },
        activeInstanceData() {
            if (!this.activeInstanceIid) {
                return null
            }
            return this.spaceInstances.find(instance => instance.iid.toString() === this.activeInstanceIid.toString()) || null
        },
        canDeleteActiveInstance() {
            return Boolean(
                this.isSpaceAdmin &&
                    this.activeInstanceData &&
                    this.masterInstance?.iid !== undefined &&
                    this.masterInstance?.dev_snid !== undefined &&
                    !isMasterOrDistributedInstance(this.activeInstanceData.long_id)
            )
        }
    },
    methods: {
        instanceRole,
        goToInstanceSettings() {
            this.instanceActionsMenu = false
            if (!this.activeOrgOid || !this.activeSpaceSid || !this.activeInstanceIid) {
                return
            }
            this.$router.push({
                name: 'config-instance-general',
                params: {
                    oid: this.activeOrgOid,
                    sid: this.activeSpaceSid,
                    iid: this.activeInstanceIid
                }
            })
        },
        isInstanceSelected(iid) {
            if (this.$route.params.iid === undefined || iid === undefined) {
                return false
            }
            return this.$route.params.iid.toString() === iid.toString()
        },
        getInstanceTypeIcon(instanceName) {
            return getInstanceTypeIcon(instanceName)
        },
        redirectToConfigInstance(iid) {
            const sid = this.$route.params.sid
            if (!this.isConfigRoute || sid === undefined || iid === undefined) {
                return false
            }

            const isAlreadyOnTargetConfigInstance =
                this.$route.name === 'config-instance-general' &&
                this.$route.params.sid?.toString() === sid.toString() &&
                this.$route.params.iid?.toString() === iid.toString()

            if (isAlreadyOnTargetConfigInstance) {
                return true
            }

            this.$router.push({
                name: 'config-instance-general',
                params: { ...this.$route.params, sid, iid },
                query: { ...this.$route.query }
            })
            return true
        },
        gotoInstances() {
            this.instanceMenu = false
            if (this.isConfigRoute) {
                if (this.redirectToConfigInstance(this.$route.params.iid)) {
                    return
                }
                return
            }
            this.$router.push({ name: 'space-instances', params: { ...this.$route.params } })
        },
        loadConfigInstances(force = false) {
            if (!this.isConfigRoute || this.$route.params.sid === undefined) {
                return
            }

            const sid = this.$route.params.sid.toString()
            if (!force && this.loadedConfigInstancesSid === sid) {
                return
            }
            if (this.fetchingInstances) {
                return
            }

            this.loadedConfigInstancesSid = sid
            this.$store.dispatch('spaceStore/fetchSpaceInstances', this.$route.params.sid)
        },
        showInstanceOpenSnackBar(instanceName) {
            this.$store.dispatch('showSnackBar', {
                snackBarText: `Moved to instance: ${instanceName}`,
                snackBarTimeout: 5000,
                snackBarIcon: 'check_circle'
            })
        },
        goToMaster() {
            this.openInstance('master', this.masterInstance.iid, this.masterInstance.role)
        },
        fetchDistributedInstanceSnapshots(iid, routeName = 'snapshot-overview', extraParams = {}) {
            this.$axios
                .get(`instances/${iid}/snapshots`)
                .then(response => {
                    if (response.data && response.data.length < 2) {
                        const delay = 2000
                        this.$store.dispatch('showSnackBar', {
                            snackBarText: 'Distributed has no snapshots, resetting to master instance.',
                            snackBarTimeout: delay,
                            snackBarIcon: 'info'
                        })
                        setTimeout(() => {
                            this.goToMaster()
                        }, delay)
                    } else if (response.data && response.data.length > 1) {
                        const sortedSnapshots = sortArray(response.data, 'snid', 'descending', false)
                        const nonDevelopmentSnapshots = sortedSnapshots.filter(snapshot => snapshot.long_id !== 'Current State')
                        const mostRecentSnapshot = nonDevelopmentSnapshots[0]
                        if (this.redirectToConfigInstance(mostRecentSnapshot.iid)) {
                            this.showInstanceOpenSnackBar(mostRecentSnapshot.long_id)
                            return
                        }
                        this.$router.push({
                            name: routeName,
                            params: {
                                oid: this.$route.params.oid,
                                sid: this.$route.params.sid,
                                iid: mostRecentSnapshot.iid,
                                snid: mostRecentSnapshot.snid,
                                ...extraParams
                            }
                        })
                        this.showInstanceOpenSnackBar(mostRecentSnapshot.long_id)
                    }
                })
                .catch(error => {
                    console.log(error)
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: 'Failed to fetch snapshots of distributed',
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                })
        },
        async openInstance(instanceName, iid, role) {
            this.instanceMenu = false
            if (this.redirectToConfigInstance(iid)) {
                this.showInstanceOpenSnackBar(instanceName)
                return
            }
            await this.$store.dispatch('instanceStore/fetchSnapshots', iid)
            if (isEditorRole(role) && !isDistributedInstance(instanceName)) {
                const sortedInstanceSnapshots = sortArray(this.instanceSnapshots, 'snid', 'ascending', false)
                if (sortedInstanceSnapshots.length) {
                    this.$router.push({
                        name: 'snapshot-overview',
                        params: { oid: this.$route.params.oid, sid: this.$route.params.sid, iid, snid: sortedInstanceSnapshots[0].snid }
                    })
                    this.showInstanceOpenSnackBar(instanceName)
                }
            } else if (isDistributedInstance(instanceName)) {
                this.fetchDistributedInstanceSnapshots(iid)
            } else if (isViewerRole(role)) {
                const sortedInstanceSnapshots = sortArray(this.instanceSnapshots, 'snid', 'descending', false)
                this.$router.push({
                    name: 'snapshot-overview',
                    params: { oid: this.$route.params.oid, sid: this.$route.params.sid, iid, snid: sortedInstanceSnapshots[0].snid }
                })
                this.showInstanceOpenSnackBar(instanceName)
            }
        },
        disabledInstance(snapshots) {
            if (!Array.isArray(snapshots)) {
                return false
            }
            return snapshots.length === 0
        }
    },
    watch: {
        '$route.params.sid': {
            immediate: true,
            handler(newSid, oldSid) {
                if (newSid === undefined) {
                    this.loadedConfigInstancesSid = null
                    return
                }

                if (oldSid === undefined || newSid.toString() !== oldSid.toString()) {
                    this.loadedConfigInstancesSid = null
                }

                this.loadConfigInstances()
            }
        },
        '$route.name'(newName, oldName) {
            const enteredConfigRoute = (newName || '').startsWith('config') && !(oldName || '').startsWith('config')
            if (enteredConfigRoute) {
                this.loadConfigInstances()
            }
        }
    }
}
</script>
